import { useCallback } from "react";
import type { Component, Page, Section } from "../content/schema-types";
import { useLanguage, type ComponentByType, type PageByType, type SectionByType } from "../contexts/LanguageContext";
import { overrideContent } from "../utils";

/**
 * Standard content API hook
 *
 * Provides a consistent way to access and merge content from Velite collections.
 * Uses generic type parameters for type-safe returns.
 *
 * @example
 * ```typescript
 * import { type HomepageProps } from "../content/page-homepage";
 * import { type ContactSectionProps } from "../content/section-contact";
 *
 * const { getHeader, getFooter, getSectionContent, getPageContent } = useContent();
 *
 * // Type-safe page content
 * const content = getPageContent<HomepageProps>("homepage");
 *
 * // Type-safe section content
 * const contact = getSectionContent<ContactSectionProps>("contact");
 * ```
 */
export const useContent = () => {
    const { content: i18nContent, language } = useLanguage();

    /**
     * Get header props for Header component with optional page-level override
     *
     * @param override - Optional header override from page content (e.g., from page.header)
     * @returns Merged header props with override applied
     *
     * @example
     * ```typescript
     * // Get default header
     * const header = getHeader();
     *
     * // Get header with page override
     * const pageContent = getPageContent<PlatformPageProps>("platformPage");
     * const header = getHeader(pageContent?.header);
     * ```
     */
    const getHeader = useCallback(
        (
            override?: Partial<ComponentByType<"header">["content"]> | null
        ): ComponentByType<"header">["content"] | undefined => {
            const defaultHeader = i18nContent.components?.header?.content as
                | ComponentByType<"header">["content"]
                | undefined;
            if (!defaultHeader) {
                return override as ComponentByType<"header">["content"] | undefined;
            }
            return overrideContent(defaultHeader, override);
        },
        [i18nContent]
    );

    /**
     * Get footer props for Footer component
     */
    const getFooter = useCallback(
        (
            override?: Partial<ComponentByType<"footer">["content"]> | null
        ): ComponentByType<"footer">["content"] | undefined => {
            const defaultFooter = i18nContent.components?.footer?.content as
                | ComponentByType<"footer">["content"]
                | undefined;
            if (!defaultFooter) {
                return override as ComponentByType<"footer">["content"] | undefined;
            }
            return overrideContent(defaultFooter, override);
        },
        [i18nContent]
    );

    /**
     * Get section by key (returns full section object with metadata)
     */
    const getSection: <T extends Section["type"]>(sectionKey: string) => SectionByType<T> | undefined = useCallback(
        <T extends Section["type"]>(sectionKey: string): SectionByType<T> | undefined => {
            return i18nContent.sections[sectionKey] as SectionByType<T> | undefined;
        },
        [i18nContent]
    );

    /**
     * Get section content with optional page-level override
     *
     * @typeParam T - The section content type (import from schema file)
     * @example
     * ```typescript
     * import { type ContactSectionProps } from "../content/section-contact";
     * const contact = getSectionContent<ContactSectionProps>("contact");
     * ```
     */
    const getSectionContent: <T>(sectionKey: string, propsOverride?: Partial<T> | null) => T | undefined = useCallback(
        <T>(sectionKey: string, pageOverride?: Partial<T> | null): T | undefined => {
            const defaultSection = i18nContent.sections[sectionKey];
            if (!defaultSection) {
                return pageOverride as T | undefined;
            }
            return overrideContent(defaultSection.content as T, pageOverride);
        },
        [i18nContent]
    );

    /**
     * Get page by key (returns full page object with metadata)
     */
    const getPage: <T extends Page["type"]>(pageKey: string) => PageByType<T> | undefined = useCallback(
        <T extends Page["type"]>(pageKey: string): PageByType<T> | undefined => {
            return i18nContent.pages[pageKey] as PageByType<T> | undefined;
        },
        [i18nContent]
    );

    /**
     * Get page content with optional props override
     *
     * @typeParam T - The page content type (import from schema file)
     * @example
     * ```typescript
     * import { type HomepageProps } from "../content/page-homepage";
     * const content = getPageContent<HomepageProps>("homepage");
     * ```
     */
    const getPageContent: <T>(pageKey: string, propsOverride?: Partial<T> | null) => T | undefined = useCallback(
        <T>(pageKey: string, propsOverride?: Partial<T> | null): T | undefined => {
            const page = i18nContent.pages[pageKey];
            if (!page?.content) {
                return propsOverride as T | undefined;
            }
            return overrideContent(page.content as T, propsOverride);
        },
        [i18nContent]
    );

    /**
     * Get component content by key
     *
     * @typeParam T - The component content type
     */
    const getComponent: <T extends Component["type"]>(componentKey: string) => ComponentByType<T> | undefined =
        useCallback(
            <T>(componentKey: string): T | undefined => {
                const component = i18nContent.components[componentKey];
                return component?.content as T | undefined;
            },
            [i18nContent]
        );

    /**
     * Get component content with optional override
     *
     * @typeParam T - The component content type
     */
    const getComponentContent: <T>(componentKey: string, propsOverride?: Partial<T> | null) => T | undefined =
        useCallback(
            <T>(componentKey: string, propsOverride?: Partial<T> | null): T | undefined => {
                const component = i18nContent.components[componentKey];
                if (!component?.content) {
                    return propsOverride as T | undefined;
                }
                return overrideContent(component.content as T, propsOverride);
            },
            [i18nContent]
        );

    /**
     * Get blog post by key
     *
     * @param postKey - camelCase key for the blog post
     * @returns Blog post object
     */
    const getBlogPost = useCallback(
        (postKey: string) => {
            return i18nContent.blog[postKey];
        },
        [i18nContent.blog]
    );

    return {
        // Getters
        getHeader,
        getFooter,
        getSection,
        getSectionContent,
        getPage,
        getPageContent,
        getComponent,
        getComponentContent,
        getBlogPost,

        // Direct access (for advanced use cases)
        common: i18nContent.common,
        pages: i18nContent.pages,
        sections: i18nContent.sections,
        components: i18nContent.components,
        blog: i18nContent.blog,
        stories: i18nContent.stories,
        portfolio: i18nContent.portfolio,

        // Current language
        language,
    };
};

export default useContent;
